import { Injectable } from '@angular/core';
import { RouteRequest, RouteResult } from './map-provider.service';

@Injectable({
  providedIn: 'root'
})
export class RouteDiagnosticService {
  private routeHistory: Array<{
    timestamp: Date;
    component: string;
    request: RouteRequest;
    result: RouteResult;
    provider: string;
  }> = [];

  /**
   * Enregistre une requête de route pour diagnostic
   */
  logRouteRequest(component: string, request: RouteRequest, provider: string): void {
    console.log(`🔍 [${component}] Route Request (${provider}):`, {
      origin: this.normalizeCoordinates(request.origin),
      destination: this.normalizeCoordinates(request.destination),
      waypoints: request.waypoints?.map(wp => this.normalizeCoordinates(wp.location || wp)),
      travelMode: request.travelMode,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Enregistre un résultat de route pour diagnostic
   */
  logRouteResult(component: string, result: RouteResult, provider: string): void {
    const routeInfo = {
      distance: result.distance,
      duration: result.duration,
      totalDistanceMeters: (result.distance.Km * 1000) + result.distance.M,
      totalDurationMinutes: (result.duration.hours * 60) + result.duration.minutes,
      timestamp: new Date().toISOString()
    };

    console.log(`✅ [${component}] Route Result (${provider}):`, routeInfo);

    // Stocker dans l'historique pour comparaison
    this.routeHistory.push({
      timestamp: new Date(),
      component,
      request: {} as RouteRequest, // On ne stocke pas la requête complète pour économiser la mémoire
      result,
      provider
    });

    // Garder seulement les 10 dernières entrées
    if (this.routeHistory.length > 10) {
      this.routeHistory.shift();
    }

    // Analyser les incohérences
    this.analyzeInconsistencies();
  }

  /**
   * Normalise les coordonnées pour la comparaison
   */
  private normalizeCoordinates(position: any): { lat: number; lng: number } {
    return {
      lat: position.lat || position.latitude,
      lng: position.lng || position.longitude
    };
  }

  /**
   * Analyse les incohérences dans les résultats de route
   */
  private analyzeInconsistencies(): void {
    if (this.routeHistory.length < 2) return;

    const recentRoutes = this.routeHistory.slice(-5); // 5 dernières routes
    
    // Comparer les distances pour des routes similaires
    for (let i = 0; i < recentRoutes.length - 1; i++) {
      for (let j = i + 1; j < recentRoutes.length; j++) {
        const route1 = recentRoutes[i];
        const route2 = recentRoutes[j];
        
        const distance1 = (route1.result.distance.Km * 1000) + route1.result.distance.M;
        const distance2 = (route2.result.distance.Km * 1000) + route2.result.distance.M;
        
        // Si les distances sont très différentes (plus de 50% de différence)
        const ratio = Math.max(distance1, distance2) / Math.min(distance1, distance2);
        
        if (ratio > 1.5) {
          console.warn(`⚠️ INCONSISTENCE DÉTECTÉE:`, {
            route1: {
              component: route1.component,
              provider: route1.provider,
              distance: route1.result.distance,
              totalMeters: distance1,
              timestamp: route1.timestamp
            },
            route2: {
              component: route2.component,
              provider: route2.provider,
              distance: route2.result.distance,
              totalMeters: distance2,
              timestamp: route2.timestamp
            },
            ratio: ratio.toFixed(2),
            difference: Math.abs(distance1 - distance2)
          });
        }
      }
    }
  }

  /**
   * Génère un rapport de diagnostic
   */
  generateDiagnosticReport(): void {
    console.log('📊 RAPPORT DE DIAGNOSTIC DES ROUTES:');
    console.log('=====================================');
    
    if (this.routeHistory.length === 0) {
      console.log('Aucune route enregistrée');
      return;
    }

    // Statistiques par provider
    const providerStats = this.routeHistory.reduce((acc, route) => {
      if (!acc[route.provider]) {
        acc[route.provider] = {
          count: 0,
          totalDistance: 0,
          distances: []
        };
      }
      
      const distanceMeters = (route.result.distance.Km * 1000) + route.result.distance.M;
      acc[route.provider].count++;
      acc[route.provider].totalDistance += distanceMeters;
      acc[route.provider].distances.push(distanceMeters);
      
      return acc;
    }, {} as any);

    Object.entries(providerStats).forEach(([provider, stats]: [string, any]) => {
      const avgDistance = stats.totalDistance / stats.count;
      const minDistance = Math.min(...stats.distances);
      const maxDistance = Math.max(...stats.distances);
      
      console.log(`\n${provider}:`);
      console.log(`  - Nombre de routes: ${stats.count}`);
      console.log(`  - Distance moyenne: ${(avgDistance / 1000).toFixed(2)} km`);
      console.log(`  - Distance min: ${(minDistance / 1000).toFixed(2)} km`);
      console.log(`  - Distance max: ${(maxDistance / 1000).toFixed(2)} km`);
      console.log(`  - Écart: ${((maxDistance - minDistance) / 1000).toFixed(2)} km`);
    });

    // Routes récentes
    console.log('\nRoutes récentes:');
    this.routeHistory.slice(-5).forEach((route, index) => {
      const distanceMeters = (route.result.distance.Km * 1000) + route.result.distance.M;
      console.log(`  ${index + 1}. [${route.component}] ${route.provider}: ${(distanceMeters / 1000).toFixed(2)} km (${route.timestamp.toLocaleTimeString()})`);
    });
  }

  /**
   * Efface l'historique
   */
  clearHistory(): void {
    this.routeHistory = [];
    console.log('🗑️ Historique des routes effacé');
  }

  /**
   * Diagnostique les différences de distance pour un trajet spécifique
   */
  diagnoseDistanceDifferences(origin: any, destination: any): void {
    const directDistance = this.calculateDirectDistance(origin, destination);
    
    console.log('🔍 Diagnostic des distances:', {
      origin: this.normalizeCoordinates(origin),
      destination: this.normalizeCoordinates(destination),
      directDistanceMeters: directDistance,
      directDistanceKm: (directDistance / 1000).toFixed(2)
    });

    // Comparer avec les routes récentes dans l'historique
    if (this.routeHistory.length > 0) {
      const recentRoutes = this.routeHistory.slice(-3);
      console.log('📊 Routes récentes pour comparaison:', recentRoutes.map(route => ({
        component: route.component,
        provider: route.provider,
        distance: route.result.distance,
        totalMeters: (route.result.distance.Km * 1000) + route.result.distance.M,
        differenceFromDirect: Math.abs(((route.result.distance.Km * 1000) + route.result.distance.M) - directDistance)
      })));
    }
  }

  /**
   * Vérifie la cohérence des distances pour un trajet donné
   */
  checkDistanceConsistency(origin: any, destination: any, tolerance: number = 5000): boolean {
    const directDistance = this.calculateDirectDistance(origin, destination);
    const recentRoutes = this.routeHistory.slice(-5);
    
    if (recentRoutes.length < 2) {
      console.log('⚠️ Pas assez de routes récentes pour vérifier la cohérence');
      return true;
    }

    const routeDistances = recentRoutes.map(route => ({
      component: route.component,
      provider: route.provider,
      distance: (route.result.distance.Km * 1000) + route.result.distance.M
    }));

    // Vérifier les différences entre les routes
    for (let i = 0; i < routeDistances.length - 1; i++) {
      for (let j = i + 1; j < routeDistances.length; j++) {
        const diff = Math.abs(routeDistances[i].distance - routeDistances[j].distance);
        if (diff > tolerance) {
          console.warn(`🚨 INCOHÉRENCE DÉTECTÉE: Différence de ${diff}m entre:`, {
            route1: routeDistances[i],
            route2: routeDistances[j],
            tolerance
          });
          return false;
        }
      }
    }

    console.log('✅ Cohérence des distances vérifiée');
    return true;
  }

  /**
   * Calcule la distance en ligne droite entre deux points (formule de Haversine)
   */
  private calculateDirectDistance(origin: any, destination: any): number {
    const originCoords = this.normalizeCoordinates(origin);
    const destCoords = this.normalizeCoordinates(destination);
    
    const R = 6371e3; // Rayon de la Terre en mètres
    const φ1 = originCoords.lat * Math.PI / 180;
    const φ2 = destCoords.lat * Math.PI / 180;
    const Δφ = (destCoords.lat - originCoords.lat) * Math.PI / 180;
    const Δλ = (destCoords.lng - originCoords.lng) * Math.PI / 180;

    const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ/2) * Math.sin(Δλ/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

    return R * c; // Distance en mètres
  }
} 